import { Box, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import type { NewlyNotReportingFacilitiesQuery } from "generated-graphql/graphql";
import { useOpenNewlyNotReportingReports } from "hooks/useOpenNewlyNotReportingReports";
import pluralize from "pluralize";
import { useCallback, useState } from "react";
import { NEWLY_NOT_REPORTING_FACILITIES_QUERY } from "queries/newlyNotReportingFacilities";

type NewlyNotReportingFacility =
  NewlyNotReportingFacilitiesQuery["newlyNotReportingFacilities"]["items"][number];

const columns: OmnisearchGridColDef<NewlyNotReportingFacility>[] = [
  {
    field: "tenantName",
    headerName: "Organization",
    flex: 1,
    minWidth: 120,
  },
  {
    field: "facilityName",
    headerName: "Facility",
    flex: 1,
    minWidth: 120,
  },
  {
    field: "state",
    headerName: "State",
    width: 70,
    valueGetter: (_, row) => row.state ?? "",
  },
  {
    field: "notReportingReason",
    headerName: "Not Reporting Reason",
    flex: 1,
    minWidth: 140,
    valueGetter: (_, row) => row.notReportingReason ?? "",
  },
  {
    field: "encampStatusPreviousYear",
    headerName: "Prev. Year Status",
    width: 120,
    valueGetter: (_, row) => row.encampStatusPreviousYear ?? "",
  },
  {
    field: "encampStatusCurrentYear",
    headerName: "Current Year Status",
    width: 140,
    valueGetter: (_, row) => row.encampStatusCurrentYear ?? "",
  },
];

type Props = {
  open: boolean;
  onClose: () => void;
  reportingYear: number;
};

export function NewlyNotReportingFacilitiesDialog({
  open,
  onClose,
  reportingYear,
}: Props) {
  const alerts = useAlerts();
  const [selectedFacilityIds, setSelectedFacilityIds] = useState<string[]>([]);
  const [openNewlyNotReportingReports, { loading }] =
    useOpenNewlyNotReportingReports();

  const handleSelectedIdsChanged = useCallback((ids: (string | number)[]) => {
    setSelectedFacilityIds(ids.map(String));
  }, []);

  const handleCreateReports = useCallback(async () => {
    if (selectedFacilityIds.length === 0) return;
    try {
      const result = await openNewlyNotReportingReports({
        variables: { facilityIds: selectedFacilityIds, reportingYear },
      });
      const createdCount =
        result.data?.openNewlyNotReportingReports.createdCount ?? 0;
      alerts.success(
        `Opened ${createdCount} newly not reporting ${pluralize(
          "report",
          createdCount
        )}.`
      );
      setSelectedFacilityIds([]);
      onClose();
    } catch (error) {
      alerts.error("Error opening newly not reporting reports", error);
    }
  }, [
    selectedFacilityIds,
    reportingYear,
    openNewlyNotReportingReports,
    alerts,
    onClose,
  ]);

  const hasSelection = selectedFacilityIds.length > 0;
  const primaryLabel = hasSelection
    ? `Create ${selectedFacilityIds.length} ${pluralize(
        "report",
        selectedFacilityIds.length
      )}`
    : "Close";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Newly Not Reporting Facilities"
      fullWidth
      maxWidth="lg"
      hideCancelButton={!hasSelection}
      primaryActionButtonProps={{
        saveText: loading ? "Creating…" : primaryLabel,
        onClick: hasSelection ? handleCreateReports : onClose,
        loading,
        disabled: hasSelection && loading,
        hideStartIcon: !hasSelection,
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Facilities that reported in the previous year but are not reporting for
        the selected year (RY{reportingYear}). Select rows and click Create to
        open newly not reporting reports for the selected facilities.
      </Typography>
      <Box sx={{ minHeight: 400 }}>
        <OmnisearchDataGrid<
          NewlyNotReportingFacilitiesQuery,
          { reportingYear: number },
          NewlyNotReportingFacility
        >
          persistenceKey="newly-not-reporting-facilities-dialog"
          columns={columns}
          dataQuery={NEWLY_NOT_REPORTING_FACILITIES_QUERY}
          dataQueryVariables={{ reportingYear }}
          getItems={(data) => data.newlyNotReportingFacilities.items}
          getCount={(data) => data.newlyNotReportingFacilities.count}
          getRowId={(row) => row.facilityId}
          isURLDriven={false}
          skip={!open}
          noDataMessage="No newly not reporting facilities for this year."
          initialPageSize={25}
          rowHeight={40}
          gridSx={{
            minHeight: 400,
            maxHeight: "60vh",
          }}
          checkboxSelection
          selectedIds={selectedFacilityIds}
          onSelectedIdsChanged={handleSelectedIdsChanged}
        />
      </Box>
    </Dialog>
  );
}
